import { ScrapingError, toScrapingError } from "./errors";
import { supabase } from "./supabase";

export interface ErrorLog {
  id?: string;
  timestamp: string;
  errorType: string;
  errorCode: string;
  message: string;
  stack?: string;
  recoverable: boolean;
  metadata?: Record<string, unknown>;
  userId?: string;
  sessionId?: string;
  url?: string;
  browser?: string;
  severity: "low" | "medium" | "high" | "critical";
}

export class ErrorLogger {
  private static instance: ErrorLogger;
  private readonly DEBUG = process.env.NODE_ENV !== "production";
  private readonly MAX_RETRY_ATTEMPTS = 3;
  private readonly RETRY_DELAY = 1000; // ms
  private sessionId: string;
  private userId?: string;

  private constructor() {
    this.sessionId = this.generateSessionId();
  }

  public static getInstance(): ErrorLogger {
    if (!ErrorLogger.instance) {
      ErrorLogger.instance = new ErrorLogger();
    }
    return ErrorLogger.instance;
  }

  /**
   * Set the current user ID for error tracking
   */
  public setUserId(userId: string): void {
    this.userId = userId;
  }

  /**
   * Generate a unique session ID
   */
  private generateSessionId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substring(2);
  }

  /**
   * Determine error severity based on error type and recoverable status
   */
  private determineSeverity(
    error: Error,
  ): "low" | "medium" | "high" | "critical" {
    if (!(error instanceof ScrapingError)) {
      return "high"; // Unknown errors are high severity
    }

    if (!error.recoverable) {
      return "critical";
    }

    switch (error.code) {
      case "NETWORK_ERROR":
      case "TIMEOUT_ERROR":
        return "medium";
      case "SELECTOR_ERROR":
      case "VALIDATION_ERROR":
        return "low";
      case "AUTHENTICATION_ERROR":
      case "DATABASE_ERROR":
        return "high";
      default:
        return "medium";
    }
  }

  /**
   * Get browser and environment information
   */
  private getBrowserInfo(): string {
    try {
      return `${navigator.userAgent} | ${window.innerWidth}x${window.innerHeight}`;
    } catch (e) {
      return "Unknown browser";
    }
  }

  /**
   * Log an error with retry mechanism
   */
  async logError(
    error: Error,
    metadata?: Record<string, unknown>,
    retryAttempt = 0,
  ): Promise<void> {
    // Convert to ScrapingError if it's not already one
    const scrapingError =
      error instanceof ScrapingError ? error : toScrapingError(error);

    const severity = this.determineSeverity(scrapingError);

    const errorLog: ErrorLog = {
      timestamp: new Date().toISOString(),
      errorType: scrapingError.name,
      errorCode:
        scrapingError instanceof ScrapingError
          ? scrapingError.code
          : "UNKNOWN_ERROR",
      message: scrapingError.message,
      stack: scrapingError.stack,
      recoverable:
        scrapingError instanceof ScrapingError
          ? scrapingError.recoverable
          : false,
      metadata: {
        ...metadata,
        ...(scrapingError instanceof ScrapingError
          ? scrapingError.details
          : {}),
      },
      userId: this.userId,
      sessionId: this.sessionId,
      url: typeof window !== "undefined" ? window.location.href : undefined,
      browser:
        typeof window !== "undefined" ? this.getBrowserInfo() : undefined,
      severity,
    };

    // Console logging in development
    if (this.DEBUG) {
      console.error("Error logged:", {
        ...errorLog,
        metadata: errorLog.metadata
          ? JSON.stringify(errorLog.metadata, null, 2)
          : undefined,
      });
    }

    try {
      // Store in Supabase
      const { error: dbError } = await supabase
        .from("error_logs")
        .insert(errorLog);

      if (dbError) {
        if (this.DEBUG) {
          console.error("Failed to store error log:", dbError);
        }

        // Retry logic
        if (retryAttempt < this.MAX_RETRY_ATTEMPTS) {
          setTimeout(
            () => {
              this.logError(error, metadata, retryAttempt + 1);
            },
            this.RETRY_DELAY * Math.pow(2, retryAttempt),
          ); // Exponential backoff
        }
      }
    } catch (dbError) {
      if (this.DEBUG) {
        console.error("Failed to store error log:", dbError);
      }
    }
  }

  /**
   * Get recent errors with filtering options
   */
  async getRecentErrors(
    options: {
      limit?: number;
      severity?: "low" | "medium" | "high" | "critical";
      errorType?: string;
      recoverable?: boolean;
      fromDate?: Date;
      toDate?: Date;
    } = {},
  ): Promise<ErrorLog[]> {
    const {
      limit = 100,
      severity,
      errorType,
      recoverable,
      fromDate,
      toDate,
    } = options;

    let query = supabase
      .from("error_logs")
      .select("*")
      .order("timestamp", { ascending: false })
      .limit(limit);

    if (severity) {
      query = query.eq("severity", severity);
    }

    if (errorType) {
      query = query.eq("errorType", errorType);
    }

    if (recoverable !== undefined) {
      query = query.eq("recoverable", recoverable);
    }

    if (fromDate) {
      query = query.gte("timestamp", fromDate.toISOString());
    }

    if (toDate) {
      query = query.lte("timestamp", toDate.toISOString());
    }

    const { data, error } = await query;

    if (error) {
      console.error("Failed to fetch error logs:", error);
      return [];
    }

    return data;
  }

  /**
   * Get error statistics
   */
  async getErrorStats(): Promise<{
    total: number;
    recoverable: number;
    nonRecoverable: number;
    byType: Record<string, number>;
    bySeverity: Record<string, number>;
    recentTrend: { date: string; count: number }[];
  }> {
    const { data, error } = await supabase.from("error_logs").select("*");

    if (error) {
      console.error("Failed to fetch error stats:", error);
      return {
        total: 0,
        recoverable: 0,
        nonRecoverable: 0,
        byType: {},
        bySeverity: {},
        recentTrend: [],
      };
    }

    // Calculate stats
    const stats = data.reduce(
      (acc, log) => {
        acc.total++;
        if (log.recoverable) acc.recoverable++;
        else acc.nonRecoverable++;
        acc.byType[log.errorType] = (acc.byType[log.errorType] || 0) + 1;
        acc.bySeverity[log.severity] = (acc.bySeverity[log.severity] || 0) + 1;
        return acc;
      },
      {
        total: 0,
        recoverable: 0,
        nonRecoverable: 0,
        byType: {} as Record<string, number>,
        bySeverity: {} as Record<string, number>,
      },
    );

    // Calculate trend for the last 7 days
    const now = new Date();
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const recentTrend: { date: string; count: number }[] = [];

    // Initialize array with zeros for the last 7 days
    for (let i = 0; i < 7; i++) {
      const date = new Date(sevenDaysAgo.getTime() + i * 24 * 60 * 60 * 1000);
      recentTrend.push({
        date: date.toISOString().split("T")[0],
        count: 0,
      });
    }

    // Count errors by day
    data.forEach((log) => {
      const logDate = new Date(log.timestamp);
      if (logDate >= sevenDaysAgo) {
        const dateStr = logDate.toISOString().split("T")[0];
        const dayIndex = recentTrend.findIndex((item) => item.date === dateStr);
        if (dayIndex !== -1) {
          recentTrend[dayIndex].count++;
        }
      }
    });

    return { ...stats, recentTrend };
  }
}

/**
 * Global error handler setup
 */
export function setupGlobalErrorHandling(): void {
  const errorLogger = ErrorLogger.getInstance();

  // Handle unhandled promise rejections
  window.addEventListener("unhandledrejection", async (event) => {
    const error =
      event.reason instanceof Error
        ? event.reason
        : new Error(String(event.reason));

    await errorLogger.logError(error, {
      type: "unhandledrejection",
      promiseReason: String(event.reason),
    });
  });

  // Handle uncaught exceptions
  window.addEventListener("error", async (event) => {
    // Prevent logging the same error twice
    if (event.error) {
      await errorLogger.logError(event.error, {
        type: "window.error",
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    } else {
      // For errors without an error object (e.g., syntax errors)
      await errorLogger.logError(new Error(event.message), {
        type: "window.error",
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        message: event.message,
      });
    }
  });
}
